import type { Ref } from 'vue';

import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

function toNumber(v: unknown): number | null {
	if (v == null || v === '') return null;
	const n = Number(v);
	return isNaN(n) ? null : n;
}

function toArrayStrings(v: unknown): string[] {
	if (v == null) return [];
	if (Array.isArray(v)) return v.map(item => String(item));
	return [String(v)];
}

/** Composable: query de rota com valor numérico. Funciona em SSR + cliente. */
export function useRouteQueryNumber(
	key: string,
	defaultValue: number | null = null
): Ref<number | null> {
	const route = useRoute();
	const value = ref<number | null>(toNumber(route.query[key]) ?? defaultValue);

	// route → ref (SSR + cliente)
	watch(
		() => route.query[key],
		newVal => {
			value.value = toNumber(newVal) ?? defaultValue;
		}
	);

	return value;
}

/** Composable: query de rota sempre como string[]. Funciona em SSR + cliente. */
export function useRouteQueryArray(
	key: string,
	defaultValue: string[] = []
): Ref<string[]> {
	const route = useRoute();
	const parseArrayQuery = (val: unknown): string[] => {
		if (Array.isArray(val))
			return val.map(toArrayStrings).flat().filter(Boolean);
		return toArrayStrings(val) || [];
	};

	const value = ref<string[]>(
		parseArrayQuery(route.query[key]) || defaultValue
	);

	// route → ref (SSR + cliente)
	watch(
		() => route.query[key],
		newVal => {
			value.value = parseArrayQuery(newVal) || defaultValue;
		}
	);

	return value;
}

/**
 * Registra sincronização bidirecional (ref → URL) para um conjunto de filtros.
 * Deve ser chamado no setup de um componente (não em stores).
 * Um único router.replace por tick para evitar race conditions.
 */
export function useSyncFiltersToRoute(
	filters: Record<string, Ref<string[] | number | null>>
) {
	if (import.meta.server) return;

	const route = useRoute();
	const router = useRouter();

	watch(
		() =>
			Object.fromEntries(Object.entries(filters).map(([k, r]) => [k, r.value])),
		values => {
			const query = { ...route.query };
			for (const [key, val] of Object.entries(values)) {
				if (val == null || (Array.isArray(val) && val.length === 0)) {
					delete query[key];
				} else {
					query[key] = Array.isArray(val) ? val : String(val);
				}
			}
			router.replace({ query });
		},
		{ deep: true }
	);
}

const DEFAULT_ROUTER_SYNC_TIMEOUT_MS = 400;

export function waitForUrlSync(
	route: any,
	timeoutMs = DEFAULT_ROUTER_SYNC_TIMEOUT_MS
): Promise<void> {
	return new Promise<void>(resolve => {
		let isResolved = false;
		let timeoutId: ReturnType<typeof setTimeout>;
		const cleanup = () => {
			if (!isResolved) {
				isResolved = true;
				unwatch();
				clearTimeout(timeoutId);
				resolve();
			}
		};
		const unwatch = watch(() => route.query, cleanup, { deep: true });
		timeoutId = setTimeout(cleanup, timeoutMs);
	});
}
